import { useState, useCallback, useRef, useEffect } from 'react'
import { DetailMode } from '@/lib/equip-types'

interface UseDetailModeOptions {
  resetKey?: string | number
}

export function isCurrentDetailRequest(
  requestId: number,
  currentRequestId: number,
  requestScopeKey: string,
  currentScopeKey: string,
): boolean {
  return requestId === currentRequestId && requestScopeKey === currentScopeKey
}

export function buildDetailResetKey(
  scopeKey: string,
  providerKey: string | null | undefined,
  year: number,
  quarter: number,
): string {
  return `${scopeKey}|${providerKey ?? 'self'}|${year}-Q${quarter}`
}

interface UseDetailModeReturn {
  detailMode: DetailMode
  isTransitioning: boolean
  handleColumnClick: (measureName: string, measureType: string) => Promise<void>
  handleTotalPatientsClick: () => void
  handleAcoPatientsClick: () => void
  handleDeselect: () => void
  toggleAcoFilter: () => void
  toggleViewMode: () => void
  toggleUnmetFilter: () => void
}

/**
 * Custom hook to manage DetailMode state machine with race condition protection.
 *
 * Orchestrates all chart-click-to-grid-swap interactions in ProgressDetailsPage.
 * Uses request versioning to prevent race conditions when users click chart bars rapidly.
 */
export function useDetailMode(options: UseDetailModeOptions = {}): UseDetailModeReturn {
  const { resetKey } = options

  const [detailMode, setDetailMode] = useState<DetailMode>({ type: 'default' })
  const [isTransitioning, setIsTransitioning] = useState(false)
  const requestIdRef = useRef(0)

  // Reset to default when resetKey changes (e.g., quarter change)
  useEffect(() => {
    if (resetKey !== undefined) {
      // Invalidate any click continuation that yielded before this scope change.
      requestIdRef.current += 1
      setDetailMode({ type: 'default' })
      setIsTransitioning(false)
    }
  }, [resetKey])

  /**
   * Handle chart bar click - transitions to quality-measure, pims, or audit mode.
   * If the same measure is already selected, deselects back to default (toggle behavior).
   */
  const handleColumnClick = useCallback(
    async (measureName: string, measureType: string) => {
      // Toggle: if same measure selected, deselect
      if (detailMode.type === 'quality-measure' && detailMode.measureName === measureName) {
        setDetailMode({ type: 'default' })
        return
      }
      if (detailMode.type === 'pims' && measureType === 'pims') {
        setDetailMode({ type: 'default' })
        return
      }
      if (
        detailMode.type === 'audit' &&
        ((measureType === 'coding' && detailMode.auditType === 'coding') ||
          (measureType === 'quality_audit' && detailMode.auditType === 'quality'))
      ) {
        setDetailMode({ type: 'default' })
        return
      }
      if (detailMode.type === 'ed-sendout' && measureType === 'ed_sendout') {
        setDetailMode({ type: 'default' })
        return
      }
      if (detailMode.type === 'quality-measure' && measureType === 'aco_visits' && detailMode.measureName === measureName) {
        setDetailMode({ type: 'default' })
        return
      }

      const currentRequestId = ++requestIdRef.current
      setIsTransitioning(true)

      // Simulate async transition (for data loading in future)
      await new Promise((resolve) => setTimeout(resolve, 0))

      // A scope reset may have invalidated this continuation while it waited.
      // Do not restore detail state from the previous provider/quarter.
      if (currentRequestId !== requestIdRef.current) return

      // Transition based on measure type
      if (measureType === 'quality') {
        setDetailMode({ type: 'quality-measure', measureName, showUnmetOnly: false })
      } else if (measureType === 'pims') {
        setDetailMode({ type: 'pims' })
      } else if (measureType === 'coding' || measureType === 'quality_audit') {
        setDetailMode({
          type: 'audit',
          auditType: measureType === 'coding' ? 'coding' : 'quality',
        })
      } else if (measureType === 'ed_sendout') {
        setDetailMode({ type: 'ed-sendout' })
      } else if (measureType === 'aco_visits') {
        setDetailMode({ type: 'quality-measure', measureName, showUnmetOnly: false })
      }

      // Guard against stale updates
      if (currentRequestId === requestIdRef.current) {
        setIsTransitioning(false)
      }
    },
    [detailMode]
  )

  /**
   * Handle "Total Patients" click - transitions to all-patients mode.
   */
  const handleTotalPatientsClick = useCallback(() => {
    setDetailMode({ type: 'all-patients', acoFilter: 'all', viewMode: 'patient' })
  }, [])

  /**
   * Handle "ACO Patients" click - transitions to all-patients mode with ACO-only filter active.
   */
  const handleAcoPatientsClick = useCallback(() => {
    setDetailMode({ type: 'all-patients', acoFilter: 'aco', viewMode: 'patient' })
  }, [])

  /**
   * Deselect - transitions back to default mode.
   */
  const handleDeselect = useCallback(() => {
    setDetailMode({ type: 'default' })
  }, [])

  /**
   * Toggle ACO filter between 'all' and 'aco' (only valid in all-patients mode).
   */
  const toggleAcoFilter = useCallback(() => {
    setDetailMode((prev) => {
      if (prev.type === 'all-patients') {
        return {
          ...prev,
          acoFilter: prev.acoFilter === 'all' ? 'aco' : 'all',
        }
      }
      return prev
    })
  }, [])

  /**
   * Toggle view mode between 'patient' and 'facility' (only valid in all-patients mode).
   */
  const toggleViewMode = useCallback(() => {
    setDetailMode((prev) => {
      if (prev.type === 'all-patients') {
        return {
          ...prev,
          viewMode: prev.viewMode === 'patient' ? 'facility' : 'patient',
        }
      }
      return prev
    })
  }, [])

  /**
   * Toggle showUnmetOnly filter (only valid in quality-measure mode).
   */
  const toggleUnmetFilter = useCallback(() => {
    setDetailMode((prev) => {
      if (prev.type === 'quality-measure') {
        return {
          ...prev,
          showUnmetOnly: !prev.showUnmetOnly,
        }
      }
      return prev
    })
  }, [])

  return {
    detailMode,
    isTransitioning,
    handleColumnClick,
    handleTotalPatientsClick,
    handleAcoPatientsClick,
    handleDeselect,
    toggleAcoFilter,
    toggleViewMode,
    toggleUnmetFilter,
  }
}
